import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, FileUp, Pencil, Plus, X } from 'lucide-react';
import type {
  ProjectoInvoice,
  ProjectoInvoiceInsert,
  ProjectoInvoiceType,
  ProjectoInvoiceStatus,
  ProjectoPLSummary,
} from '../../types/projecto';
import { PROJECTO_INVOICE_STATUS_CONFIG } from '../../types/projecto';
import { projectoInvoiceService } from '../../services/projectoService';
import { clientService } from '../../services/clientService';
import type { FulfilledInvoice } from '../../services/invoiceRequestService';
import type { Client } from '../../types/client';
import { useModal } from '../../contexts/ModalContext';
import { formatEuro } from '../../utils/currency';
import { log } from '../../lib/logger';
import { Button } from '../ui/Button';
import { FormBlock } from '../ui/FormBlock';
import { ExpandableRow, RowList, type RowTone } from '../ui/ListRows';
import { InvoiceScanModal } from '../invoice-requests/InvoiceScanModal';
import { InvoiceRequestsBlock } from '../invoice-requests/InvoiceRequestsBlock';

interface Props {
  projectoId: string;
  invoices: ProjectoInvoice[];
  onInvoicesChange: (invoices: ProjectoInvoice[]) => void;
  summary: ProjectoPLSummary;
  defaultClient: string;
  clientId: string | null;
  /** Code and name, as the person issuing the invoice reads it. */
  projectLabel: string;
  /** The client's reference usually is the purchase order. */
  reference: string;
}

const STATUSES: ProjectoInvoiceStatus[] = ['Draft', 'Issued', 'Partially Paid', 'Paid', 'Overdue', 'Cancelled'];
const TODAY = () => new Date().toISOString().slice(0, 10);
const formatDate = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString('pt-PT') : '—');

const EMPTY_INVOICE: Omit<ProjectoInvoiceInsert, 'projecto_id' | 'invoice_type'> = {
  number: null,
  counterparty_name: null,
  counterparty_email: null,
  issue_date: null,
  due_date: null,
  subtotal: 0,
  vat_rate: 0.23,
  total: 0,
  currency: 'EUR',
  status: 'Issued',
  amount_paid: 0,
  paid_date: null,
  notes: null,
  display_order: 0,
};

const STATUS_TONE: Record<ProjectoInvoiceStatus, RowTone> = {
  Draft: 'neutral',
  Issued: 'info',
  'Partially Paid': 'warning',
  Paid: 'success',
  Overdue: 'danger',
  Cancelled: 'neutral',
};

const statusOf = (invoice: ProjectoInvoice): ProjectoInvoiceStatus => (invoice.is_overdue ? 'Overdue' : invoice.status);

/** A project's invoice in the terms the invoice requests understand. */
function toRequestInvoice(invoice: ProjectoInvoice) {
  return {
    status: invoice.status === 'Draft' ? 'Not Issued' : invoice.status === 'Partially Paid' ? 'Issued' : invoice.status,
    amount_received: Number(invoice.subtotal) || 0,
    invoice_number: invoice.number ?? '',
  } as const;
}

/** What a read PDF fills in on an invoice to charge. */
function scannedFields(scanned: FulfilledInvoice, fallbackNet: number) {
  const net = scanned.net_amount ?? scanned.total_amount ?? fallbackNet;
  const total = scanned.total_amount ?? net;
  return {
    number: scanned.invoice_number || null,
    issue_date: scanned.issued_date || null,
    due_date: scanned.due_date || null,
    subtotal: net,
    vat_rate: net > 0 ? Math.round((total / net - 1) * 100) / 100 : 0.23,
    total,
    status: 'Issued' as const,
    notes: scanned.purchase_order ? `Ordem de compra ${scanned.purchase_order}` : null,
  };
}

function InvoiceForm({ invoice, invoiceType, defaultClient, onSave, onClose }: {
  invoice?: ProjectoInvoice | null;
  invoiceType: ProjectoInvoiceType;
  defaultClient: string;
  onSave: (data: Omit<ProjectoInvoiceInsert, 'projecto_id'>) => Promise<void>;
  onClose: () => void;
}) {
  const [form, setForm] = useState<Omit<ProjectoInvoiceInsert, 'projecto_id'>>(() =>
    invoice
      ? {
          invoice_type: invoice.invoice_type,
          number: invoice.number,
          counterparty_name: invoice.counterparty_name,
          counterparty_email: invoice.counterparty_email,
          issue_date: invoice.issue_date,
          due_date: invoice.due_date,
          subtotal: invoice.subtotal,
          vat_rate: invoice.vat_rate,
          total: invoice.total,
          currency: invoice.currency,
          status: invoice.status,
          amount_paid: invoice.amount_paid,
          paid_date: invoice.paid_date,
          notes: invoice.notes,
          display_order: invoice.display_order,
        }
      : { ...EMPTY_INVOICE, invoice_type: invoiceType, counterparty_name: invoiceType === 'Receivable' ? defaultClient || null : null }
  );
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const set = (u: Partial<typeof form>) => {
    setForm((f) => {
      const next = { ...f, ...u };
      if ('subtotal' in u || 'vat_rate' in u) {
        next.total = Math.round((next.subtotal ?? 0) * (1 + (next.vat_rate ?? 0)) * 100) / 100;
      }
      // Paid means paid in full; a hand-typed amount shouldn't have to say so too
      if (next.status === 'Paid' && ('status' in u || 'subtotal' in u || 'vat_rate' in u)) {
        next.amount_paid = next.total;
        next.paid_date = next.paid_date ?? TODAY();
      }
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(form);
      onClose();
    } catch {
      setSaving(false);
    }
  };

  const isReceivable = form.invoice_type === 'Receivable';
  const showPaid = form.status === 'Partially Paid' || form.status === 'Paid';

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:p-4 bg-black/40 backdrop-blur-sm" role="dialog" aria-modal="true" aria-labelledby="projecto-invoice-title">
      <div className="bg-white sm:rounded-2xl rounded-t-2xl shadow-2xl w-full max-w-xl max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between px-5 sm:px-6 py-4 border-b border-gray-200">
          <h2 id="projecto-invoice-title" className="text-base font-semibold text-gray-900">
            {invoice ? 'Editar fatura' : isReceivable ? 'Nova fatura a cobrar' : 'Nova fatura a pagar'}
          </h2>
          <button type="button" onClick={onClose} className="p-2 -mr-2 text-gray-500 hover:text-gray-900 rounded-lg hover:bg-gray-100" aria-label="Fechar">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 px-5 sm:px-6 py-5 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="cm-field">
              <label htmlFor="inv-number" className="cm-label">Número</label>
              <input id="inv-number" className="cm-input" placeholder="FT 2026/1" value={form.number ?? ''} onChange={(e) => set({ number: e.target.value || null })} />
            </div>
            <div className="cm-field">
              <label htmlFor="inv-status" className="cm-label">Estado</label>
              <select id="inv-status" className="cm-select" value={form.status} onChange={(e) => set({ status: e.target.value as ProjectoInvoiceStatus })}>
                {STATUSES.map((s) => <option key={s} value={s}>{PROJECTO_INVOICE_STATUS_CONFIG[s].label}</option>)}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="cm-field">
              <label htmlFor="inv-party" className="cm-label">{isReceivable ? 'Cliente' : 'Fornecedor'}</label>
              <input id="inv-party" className="cm-input" value={form.counterparty_name ?? ''} onChange={(e) => set({ counterparty_name: e.target.value || null })} />
            </div>
            <div className="cm-field">
              <label htmlFor="inv-email" className="cm-label">Email</label>
              <input id="inv-email" type="email" className="cm-input" value={form.counterparty_email ?? ''} onChange={(e) => set({ counterparty_email: e.target.value || null })} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="cm-field">
              <label htmlFor="inv-issue" className="cm-label">Emissão</label>
              <input id="inv-issue" type="date" className="cm-date" value={form.issue_date ?? ''} onChange={(e) => set({ issue_date: e.target.value || null })} />
            </div>
            <div className="cm-field">
              <label htmlFor="inv-due" className="cm-label">Vencimento</label>
              <input id="inv-due" type="date" className="cm-date" value={form.due_date ?? ''} onChange={(e) => set({ due_date: e.target.value || null })} />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="cm-field">
              <label htmlFor="inv-subtotal" className="cm-label">Sem IVA</label>
              <span className="cm-affix has-right">
                <input id="inv-subtotal" type="number" min="0" step="0.01" className="cm-input cm-numeric" value={form.subtotal || ''} onChange={(e) => set({ subtotal: parseFloat(e.target.value) || 0 })} />
                <span>€</span>
              </span>
            </div>
            <div className="cm-field">
              <label htmlFor="inv-vat" className="cm-label">IVA</label>
              <select id="inv-vat" className="cm-select" value={form.vat_rate ?? 0.23} onChange={(e) => set({ vat_rate: parseFloat(e.target.value) })}>
                <option value={0}>0%</option>
                <option value={0.06}>6%</option>
                <option value={0.13}>13%</option>
                <option value={0.23}>23%</option>
              </select>
            </div>
            <div className="cm-field">
              <span className="cm-label">Com IVA</span>
              <div className="cm-readonly justify-end tabular-nums font-semibold">{formatEuro(form.total ?? 0)}</div>
            </div>
          </div>

          {showPaid && (
            <div className="grid grid-cols-2 gap-4">
              <div className="cm-field">
                <label htmlFor="inv-paid" className="cm-label">{isReceivable ? 'Recebido, com IVA' : 'Pago, com IVA'}</label>
                <span className="cm-affix has-right">
                  <input id="inv-paid" type="number" min="0" step="0.01" className="cm-input cm-numeric" value={form.amount_paid || ''} onChange={(e) => set({ amount_paid: parseFloat(e.target.value) || 0 })} />
                  <span>€</span>
                </span>
              </div>
              <div className="cm-field">
                <label htmlFor="inv-paid-date" className="cm-label">Data</label>
                <input id="inv-paid-date" type="date" className="cm-date" value={form.paid_date ?? ''} onChange={(e) => set({ paid_date: e.target.value || null })} />
              </div>
            </div>
          )}

          <div className="cm-field">
            <label htmlFor="inv-notes" className="cm-label">Notas</label>
            <textarea id="inv-notes" rows={2} className="cm-textarea !min-h-[3.5rem]" value={form.notes ?? ''} onChange={(e) => set({ notes: e.target.value || null })} />
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 px-5 sm:px-6 py-3 border-t border-gray-200">
          <Button variant="secondary" size="sm" onClick={onClose}>Cancelar</Button>
          <Button variant="primary" size="sm" isLoading={saving} onClick={handleSave}>Guardar</Button>
        </div>
      </div>
    </div>,
    document.body
  );
}

function InvoiceRow({ invoice, onEdit, onDelete }: {
  invoice: ProjectoInvoice;
  onEdit: (invoice: ProjectoInvoice) => void;
  onDelete: (invoice: ProjectoInvoice) => void;
}) {
  const [open, setOpen] = useState(false);
  const status = statusOf(invoice);
  const cfg = PROJECTO_INVOICE_STATUS_CONFIG[status];
  const isReceivable = invoice.invoice_type === 'Receivable';
  const outstanding = invoice.status === 'Paid' ? 0 : Math.max(0, invoice.total - invoice.amount_paid);

  const facts: [string, string][] = [
    ['Sem IVA', formatEuro(invoice.subtotal)],
    ['IVA', `${Math.round(invoice.vat_rate * 100)}%`],
    ['Com IVA', formatEuro(invoice.total)],
    [isReceivable ? 'Por receber' : 'Por pagar', formatEuro(outstanding)],
    ['Emissão', formatDate(invoice.issue_date)],
    ['Vencimento', formatDate(invoice.due_date)],
    [isReceivable ? 'Recebida em' : 'Paga em', formatDate(invoice.paid_date)],
    ['Email', invoice.counterparty_email || '—'],
  ];

  return (
    <ExpandableRow
      tone={STATUS_TONE[status]}
      isExpanded={open}
      onToggle={() => setOpen((v) => !v)}
      ariaLabel={`Fatura ${invoice.number ?? 'sem número'}`}
      summary={
        <>
          <span className="text-sm font-medium text-gray-900">{invoice.number || 'Sem número'}</span>
          <span className={`cm-chip ${cfg.bg} ${cfg.color}`}>{cfg.label}</span>
          {invoice.counterparty_name && <span className="text-xs text-gray-500 truncate">{invoice.counterparty_name}</span>}
          {invoice.due_date && invoice.status !== 'Paid' && (
            <span className={`text-xs whitespace-nowrap ${invoice.is_overdue ? 'text-red-700 font-medium' : 'text-gray-500'}`}>vence {formatDate(invoice.due_date)}</span>
          )}
        </>
      }
      aside={formatEuro(invoice.subtotal)}
      onDelete={() => onDelete(invoice)}
      deleteLabel={`Eliminar fatura ${invoice.number ?? ''}`.trim()}
    >
      <dl className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-3">
        {facts.map(([label, value]) => (
          <div key={label} className="min-w-0">
            <dt className="text-[11px] text-gray-500">{label}</dt>
            <dd className="text-sm text-gray-900 tabular-nums truncate">{value}</dd>
          </div>
        ))}
      </dl>
      {invoice.notes && <p className="text-xs text-gray-600 whitespace-pre-wrap">{invoice.notes}</p>}
      <button type="button" onClick={() => onEdit(invoice)} className="cm-btn cm-btn-secondary cm-btn-sm">
        <Pencil className="w-3.5 h-3.5" /> Editar
      </button>
    </ExpandableRow>
  );
}

function Progress({ label, done, of, children }: { label: string; done: number; of: number; children?: React.ReactNode }) {
  const pct = of > 0 ? Math.min(100, (done / of) * 100) : 0;
  return (
    <div className="min-w-0 space-y-1.5">
      <p className="text-xs font-medium text-gray-500">{label}</p>
      <p className="text-sm text-gray-600">
        <strong className="text-base text-gray-900 tabular-nums">{formatEuro(done)}</strong> de {formatEuro(of)}
      </p>
      <div className="h-1.5 rounded-full bg-gray-100">
        <div className="h-full rounded-full bg-emerald-500 transition-all" style={{ width: `${pct}%` }} />
      </div>
      <div className="text-xs text-gray-500">{children}</div>
    </div>
  );
}

export function ProjectoInvoicesTab({ projectoId, invoices, onInvoicesChange, summary, defaultClient, clientId, projectLabel, reference }: Props) {
  const [formOpen, setFormOpen] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState<ProjectoInvoice | null>(null);
  const [formType, setFormType] = useState<ProjectoInvoiceType>('Receivable');
  const [scanning, setScanning] = useState(false);
  const [client, setClient] = useState<Client | null>(null);
  const { showConfirm, toast } = useModal();

  useEffect(() => {
    let cancelled = false;
    const load = clientId ? clientService.getById(clientId) : defaultClient ? clientService.getByName(defaultClient) : Promise.resolve(null);
    load.then((found) => { if (!cancelled) setClient(found); }).catch((err) => log.warn('[ProjectoInvoicesTab] Client not loaded:', err));
    return () => { cancelled = true; };
  }, [clientId, defaultClient]);

  const receivables = invoices.filter((i) => i.invoice_type === 'Receivable');
  const payables = invoices.filter((i) => i.invoice_type === 'Payable');
  const overInvoiced = summary.toInvoice < -0.005;

  const openAdd = (type: ProjectoInvoiceType) => {
    setEditingInvoice(null);
    setFormType(type);
    setFormOpen(true);
  };

  const openEdit = (invoice: ProjectoInvoice) => {
    setEditingInvoice(invoice);
    setFormType(invoice.invoice_type);
    setFormOpen(true);
  };

  const fail = (action: string, err: unknown) => {
    const message = err instanceof Error ? err.message : String(err);
    log.error(`[ProjectoInvoicesTab] ${action}:`, err);
    toast.error(`${action}: ${message}`);
  };

  const handleSave = async (data: Omit<ProjectoInvoiceInsert, 'projecto_id'>) => {
    try {
      if (editingInvoice) {
        const updated = await projectoInvoiceService.update(editingInvoice.id, projectoId, data);
        onInvoicesChange(invoices.map((i) => (i.id === editingInvoice.id ? updated : i)));
      } else {
        const created = await projectoInvoiceService.create({ ...data, projecto_id: projectoId, display_order: invoices.length });
        onInvoicesChange([...invoices, created]);
      }
    } catch (err) {
      fail('Não foi possível guardar a fatura', err);
      throw err;
    }
  };

  /** A PDF read in the browser becomes an issued invoice; nothing of it is kept. */
  const handleScanned = async (scanned: FulfilledInvoice) => {
    try {
      const created = await projectoInvoiceService.create({
        ...EMPTY_INVOICE,
        ...scannedFields(scanned, 0),
        projecto_id: projectoId,
        invoice_type: 'Receivable',
        counterparty_name: defaultClient || null,
        display_order: invoices.length,
      });
      onInvoicesChange([...invoices, created]);
      setScanning(false);
      toast.success('Fatura registada');
    } catch (err) {
      fail('Não foi possível registar a fatura', err);
      throw err;
    }
  };

  /** The invoice asked for arrived: it fills the draft planned for it, or is added. */
  const handleRequestedInvoice = async (requestedAmount: number, scanned: FulfilledInvoice) => {
    const planned = receivables.find((i) => i.status === 'Draft' && !i.number && Math.abs((Number(i.subtotal) || 0) - requestedAmount) < 0.01);
    try {
      if (planned) {
        const updated = await projectoInvoiceService.update(planned.id, projectoId, scannedFields(scanned, requestedAmount));
        onInvoicesChange(invoices.map((i) => (i.id === planned.id ? updated : i)));
      } else {
        const created = await projectoInvoiceService.create({
          ...EMPTY_INVOICE,
          ...scannedFields(scanned, requestedAmount),
          projecto_id: projectoId,
          invoice_type: 'Receivable',
          counterparty_name: defaultClient || null,
          display_order: invoices.length,
        });
        onInvoicesChange([...invoices, created]);
      }
    } catch (err) {
      fail('Não foi possível registar a fatura', err);
      throw err;
    }
  };

  const handleDelete = async (invoice: ProjectoInvoice) => {
    const confirmed = await showConfirm({
      title: 'Eliminar fatura?',
      message: `Remover ${invoice.number ? `a fatura ${invoice.number}` : 'esta fatura'} do projeto? Esta ação não pode ser revertida.`,
      confirmText: 'Eliminar',
      cancelText: 'Cancelar',
      variant: 'danger',
    });
    if (!confirmed) return;
    try {
      await projectoInvoiceService.delete(invoice.id, projectoId);
      onInvoicesChange(invoices.filter((i) => i.id !== invoice.id));
    } catch (err) {
      fail('Não foi possível eliminar a fatura', err);
    }
  };

  return (
    <div className="space-y-7 pb-10 pt-2">
      <FormBlock title="Ponto de situação">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
          <Progress label="Faturado ao cliente" done={summary.invoiced} of={summary.revenue}>
            {overInvoiced ? (
              <span className="inline-flex items-start gap-1 text-amber-800">
                <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-px" />
                {formatEuro(-summary.toInvoice)} acima do P&amp;L: falta alguma linha?
              </span>
            ) : summary.toInvoice > 0 ? `Falta faturar ${formatEuro(summary.toInvoice)}` : summary.revenue > 0 ? 'Tudo faturado' : 'Sem valor no P&L'}
          </Progress>
          <Progress label="Recebido do cliente" done={summary.received} of={summary.invoiced}>
            {summary.outstandingReceivables > 0 ? `Por receber ${formatEuro(summary.outstandingReceivables)} com IVA` : summary.invoiced > 0 ? 'Tudo recebido' : 'Sem faturas emitidas'}
          </Progress>
          <Progress label="Pago a fornecedores" done={summary.paidCosts} of={summary.costs}>
            {summary.pendingCosts > 0 ? `Por pagar ${formatEuro(summary.pendingCosts)}` : summary.costs > 0 ? 'Tudo pago' : 'Sem custos'}
          </Progress>
        </div>
      </FormBlock>

      <FormBlock title="Faturas a cobrar">
        {receivables.length > 0 ? (
          <RowList>
            {receivables.map((inv) => <InvoiceRow key={inv.id} invoice={inv} onEdit={openEdit} onDelete={handleDelete} />)}
          </RowList>
        ) : (
          <p className="text-sm text-gray-500">O que a agência fatura ao cliente neste projeto. Valores sem IVA.</p>
        )}
        <div className="flex flex-wrap gap-2 mt-3">
          <InvoiceRequestsBlock
            context={{ campaignId: null, projectoId, campaignName: projectLabel, purchaseOrder: reference }}
            client={client}
            totalAmount={summary.revenue}
            invoices={receivables.filter((i) => i.status !== 'Cancelled').map(toRequestInvoice)}
            onInvoiceReceived={handleRequestedInvoice}
          />
          <button type="button" onClick={() => setScanning(true)} className="cm-btn cm-btn-secondary !h-9 !px-4">
            <FileUp className="w-4 h-4" /> Carregar PDF
          </button>
          <button type="button" onClick={() => openAdd('Receivable')} className="cm-btn-add !py-1.5">
            <Plus className="w-4 h-4" /> Adicionar à mão
          </button>
        </div>
      </FormBlock>

      <FormBlock title="Faturas a pagar">
        {payables.length > 0 ? (
          <RowList>
            {payables.map((inv) => <InvoiceRow key={inv.id} invoice={inv} onEdit={openEdit} onDelete={handleDelete} />)}
          </RowList>
        ) : (
          <p className="text-sm text-gray-500">Faturas recebidas de fornecedores e talento, se as quiseres acompanhar aqui.</p>
        )}
        <button type="button" onClick={() => openAdd('Payable')} className="cm-btn-add !py-1.5 mt-3">
          <Plus className="w-4 h-4" /> Fatura a pagar
        </button>
      </FormBlock>

      {formOpen && (
        <InvoiceForm
          invoice={editingInvoice}
          invoiceType={formType}
          defaultClient={defaultClient}
          onSave={handleSave}
          onClose={() => { setFormOpen(false); setEditingInvoice(null); }}
        />
      )}

      {scanning && (
        <InvoiceScanModal
          label="Fatura a cobrar deste projeto"
          requestedAmount={0}
          clientVat={client?.vat_number ?? null}
          defaultPurchaseOrder={reference}
          onClose={() => setScanning(false)}
          onConfirm={handleScanned}
        />
      )}
    </div>
  );
}
